import React, { useState, useEffect, useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import ContentArea from '../../components/contentArea';
import CommonCard from '../../components/card/CommonCard';
import Toggle from '../../components/toggle';
import ReportCard from './components/reportCard';
import { formatDateTime, formatDateTimeDisplay, getDateRange } from '../../utils/base';
import { physicalRecordAPI } from '../../actions/imsServer';
import { API_PAGE_SIZE } from '../../constants';

export const ReportHeartbeatUnion = () => {
    const { t } = useTranslation();
    const theme = useTheme();

    // 사용자 정보
    const meInfo = JSON.parse(localStorage.getItem('meInfo') || '{}');

    // 데이터 상태 관리
    const [physicalRecords, setPhysicalRecords] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // 뷰 상태 관리
    const [activeView, setActiveView] = useState(0); // 0: 주간, 1: 월간, 2: 연간

    // 주간 데이터 상태 관리
    const [currentWeekIndex, setCurrentWeekIndex] = useState(0); // 현재 주간 인덱스 (2: 6월 1-7일)

    // 월간 데이터 상태 관리
    const [currentMonthIndex, setCurrentMonthIndex] = useState(0); // 현재 월간 인덱스 (0: 5월)

    // 연간 데이터 상태 관리
    const [currentYearIndex, setCurrentYearIndex] = useState(0); // 현재 연간 인덱스 (0: 2025년)

    // 선택된 바 인덱스 상태
    const [selectedBarIndex, setSelectedBarIndex] = useState(-1);

    // 데이터 로딩 함수
    const fetchPhysicalRecords = async () => {
        if (!meInfo.id) return;
        try {
            setLoading(true);
            setError(null);

            // 과거 3개월부터 현재까지의 데이터 로드
            const today = new Date();
            const threeMonthsAgo = new Date(today);
            threeMonthsAgo.setMonth(today.getMonth() - 3);

            const dateRange = getDateRange('yearly', threeMonthsAgo);
            let allRecords = [];
            let offset = 0;
            let page;
            do {
                const response = await physicalRecordAPI.getPhysicalRecords({
                    userId: meInfo.id,
                    state: 0,
                    startDate: dateRange.startDate,
                    endDate: dateRange.endDate,
                    offset
                });
                page = response?.data?.physicalRecords || [];
                allRecords = allRecords.concat(page);
                offset += API_PAGE_SIZE;
            } while (page.length === API_PAGE_SIZE);

            setPhysicalRecords(allRecords);
        } catch (err) {
            console.error('심박수 데이터 로딩 실패:', err);
            setError(err.message);
            setPhysicalRecords([]);
        } finally {
            setLoading(false);
        }
    };

    // 초기 데이터 로드
    useEffect(() => {
        fetchPhysicalRecords();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchPhysicalRecords는 의존성에서 제외
    }, [meInfo.id]);


    // 주간 데이터 생성 (현재 주 기준)
    const getCurrentWeekData = useMemo(() => {
        const today = new Date();
        const weekData = [];

        // 현재 주의 월요일부터 일요일까지 7일 데이터 생성
        const startOfWeek = new Date(today);
        const dayOfWeek = today.getDay();
        const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        startOfWeek.setDate(today.getDate() + mondayOffset + (currentWeekIndex * 7));

        for (let i = 0; i < 7; i++) {
            const date = new Date(startOfWeek);
            date.setDate(startOfWeek.getDate() + i);
            const dateStr = formatDateTime(date, '-', ':', 0, 10);

            // 실제 데이터에서 해당 날짜 찾기
            const actualRecord = physicalRecords.find(record => {
                const recordDate = formatDateTime(new Date(record.date), '-', ':', 0, 10);
                return recordDate === dateStr;
            });

            const minHeartRate = actualRecord?.minHeartRate || 0;
            const maxHeartRate = actualRecord?.maxHeartRate || 0;

            weekData.push({
                day: dateStr,
                min: minHeartRate,
                max: maxHeartRate,
                color: theme.palette.figma.heartRate
            });
        }

        return weekData;
    }, [physicalRecords, currentWeekIndex, theme.palette.figma.heartRate]);

    // 월간 데이터 생성 (현재 월 기준)
    const getCurrentMonthData = useMemo(() => {
        const today = new Date();
        const targetMonth = new Date(today);
        targetMonth.setMonth(today.getMonth() + currentMonthIndex);

        const year = targetMonth.getFullYear();
        const month = targetMonth.getMonth() + 1;
        const daysInMonth = new Date(year, month, 0).getDate();

        const monthData = [];

        for (let day = 1; day <= daysInMonth; day++) {
            const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

            // 실제 데이터에서 해당 날짜 찾기
            const actualRecord = physicalRecords.find(record => {
                const recordDate = formatDateTime(new Date(record.date), '-', ':', 0, 10);
                return recordDate === dateStr;
            });

            const minHeartRate = actualRecord?.minHeartRate || 0;
            const maxHeartRate = actualRecord?.maxHeartRate || 0;

            monthData.push({
                day: dateStr,
                min: minHeartRate,
                max: maxHeartRate,
                color: theme.palette.figma.heartRate
            });
        }

        return monthData;
    }, [physicalRecords, currentMonthIndex, theme.palette.figma.heartRate]);

    // 연간 데이터 생성 (현재 연도 기준)
    const getCurrentYearData = useMemo(() => {
        const today = new Date();
        const targetYear = today.getFullYear() + currentYearIndex;

        const yearData = [];

        for (let month = 1; month <= 12; month++) {
            const monthStr = `${targetYear}-${String(month).padStart(2, '0')}`;

            // 해당 월의 모든 데이터 필터링
            const monthRecords = physicalRecords.filter(record => {
                const recordDate = new Date(record.date);
                return recordDate.getFullYear() === targetYear && recordDate.getMonth() + 1 === month;
            });

            // 월별 최소/최대값 계산 (평균이 아닌 실제 최소/최대값)
            let monthlyMin = 0;
            let monthlyMax = 0;

            if (monthRecords.length > 0) {
                // 해당 월의 모든 minHeartRate 중 가장 작은 값
                const minValues = monthRecords.map(record => record.minHeartRate || 0).filter(val => val > 0);
                monthlyMin = minValues.length > 0 ? Math.min(...minValues) : 0;

                // 해당 월의 모든 maxHeartRate 중 가장 큰 값
                const maxValues = monthRecords.map(record => record.maxHeartRate || 0).filter(val => val > 0);
                monthlyMax = maxValues.length > 0 ? Math.max(...maxValues) : 0;
            }

            yearData.push({
                day: monthStr,
                min: monthlyMin,
                max: monthlyMax,
                color: theme.palette.figma.heartRate
            });
        }

        return yearData;
    }, [physicalRecords, currentYearIndex, theme.palette.figma.heartRate]);


    // Y축 값 계산
    const calculateYAxisValues = () => {
        return {
            maxValue: 180,
            minValue: 40,
            yAxisValues: [40, 60, 80, 100, 120, 140, 160]
        };
    };

    // 현재 데이터를 기반으로 통계 계산
    const calculateCurrentStats = useMemo(() => {
        let currentData;
        switch (activeView) {
            case 0:
                currentData = getCurrentWeekData;
                break;
            case 1:
                currentData = getCurrentMonthData;
                break;
            case 2:
                currentData = getCurrentYearData;
                break;
            default:
                currentData = getCurrentWeekData;
                break;
        }

        if (currentData.length === 0) {
            return {
                minHeartRate: 0,
                maxHeartRate: 0,
            };
        }

        // 0이 아닌 값들만 필터링하여 통계 계산
        const validMinValues = currentData.filter(item => item.min > 0).map(item => item.min);
        const validMaxValues = currentData.filter(item => item.max > 0).map(item => item.max);

        const minHeartRate = validMinValues.length > 0 ? Math.min(...validMinValues) : 0;
        const maxHeartRate = validMaxValues.length > 0 ? Math.max(...validMaxValues) : 0;

        return {
            minHeartRate: minHeartRate,
            maxHeartRate: maxHeartRate,
        };
    }, [activeView, getCurrentWeekData, getCurrentMonthData, getCurrentYearData]);

    // 뷰별 데이터
    const getViewData = useMemo(() => {
        switch (activeView) {
            case 0: return getCurrentWeekData;
            case 1: return getCurrentMonthData;
            case 2: return getCurrentYearData;
            default: return getCurrentWeekData;
        }
    }, [activeView, getCurrentWeekData, getCurrentMonthData, getCurrentYearData]);

    // 주간 제목 생성 (선택 언어별 날짜 표기)
    const getWeeklyTitle = useMemo(() => {
        const currentWeekData = getCurrentWeekData;
        if (currentWeekData.length === 0) return t('no_weekly_data');

        const firstDay = currentWeekData[0].day;
        const lastDay = currentWeekData[currentWeekData.length - 1].day;
        const startFormatted = formatDateTimeDisplay(firstDay, 'monthDay');
        const endFormatted = formatDateTimeDisplay(lastDay, 'monthDay');
        return `${startFormatted} ~ ${endFormatted}`;
    }, [getCurrentWeekData, t]);

    // 월간 제목 생성 (선택 언어별 연·월 표기)
    const getMonthlyTitle = useMemo(() => {
        const currentMonthData = getCurrentMonthData;
        if (currentMonthData.length === 0) return t('no_monthly_data');

        const firstDay = currentMonthData[0].day;
        const firstDate = new Date(firstDay);

        return formatDateTimeDisplay(firstDate, 'yearMonth');
    }, [getCurrentMonthData, t]);

    // 현재 월의 일수 계산
    const getCurrentMonthDays = useMemo(() => {
        const currentMonthData = getCurrentMonthData;
        if (currentMonthData.length === 0) return 31;

        const firstDay = currentMonthData[0].day;
        const firstDate = new Date(firstDay);
        const year = firstDate.getFullYear();
        const month = firstDate.getMonth() + 1;

        // 해당 월의 마지막 날짜 계산
        const lastDay = new Date(year, month, 0).getDate();
        return lastDay;
    }, [getCurrentMonthData]);

    // 연간 제목 생성 (선택 언어별 연도 표기)
    const getYearlyTitle = useMemo(() => {
        const currentYearData = getCurrentYearData;
        if (currentYearData.length === 0) return t('no_yearly_data');

        const firstDay = currentYearData[0].day;
        const firstDate = new Date(firstDay);

        return formatDateTimeDisplay(firstDate, 'year');
    }, [getCurrentYearData, t]);

    // 뷰별 제목 (0: 주간, 1: 월간, 2: 연간)
    const getViewTitle = useMemo(() => {
        const titles = [getWeeklyTitle, getMonthlyTitle, getYearlyTitle];
        return titles[activeView] ?? getWeeklyTitle;
    }, [activeView, getWeeklyTitle, getMonthlyTitle, getYearlyTitle]);

    // 주간 이동 함수들
    const handlePreviousWeek = () => {
        setCurrentWeekIndex(currentWeekIndex - 1);
        clearSelectedBar();
    };

    const handleNextWeek = () => {
        const today = new Date();
        const startOfWeek = new Date(today);
        const dayOfWeek = today.getDay();
        const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        startOfWeek.setDate(today.getDate() + mondayOffset);

        const nextWeekStart = new Date(startOfWeek);
        nextWeekStart.setDate(startOfWeek.getDate() + (currentWeekIndex + 1) * 7);

        if (nextWeekStart <= today) {
            setCurrentWeekIndex(currentWeekIndex + 1);
            clearSelectedBar();
        }
    };

    // 월간 이동 함수들
    const handlePreviousMonth = () => {
        setCurrentMonthIndex(currentMonthIndex - 1);
        clearSelectedBar();
    };

    const handleNextMonth = () => {
        const today = new Date();
        const nextMonthIndex = currentMonthIndex + 1;

        const targetMonth = new Date(today);
        targetMonth.setMonth(today.getMonth() + nextMonthIndex);
        const nextMonthFirstDay = new Date(targetMonth.getFullYear(), targetMonth.getMonth(), 1);

        if (nextMonthFirstDay <= today) {
            setCurrentMonthIndex(nextMonthIndex);
            clearSelectedBar();
        }
    };

    // 연간 이동 함수들
    const handlePreviousYear = () => {
        setCurrentYearIndex(currentYearIndex - 1);
        clearSelectedBar();
    };

    const handleNextYear = () => {
        const today = new Date();
        const nextYearIndex = currentYearIndex + 1;

        const targetYear = new Date(today);
        targetYear.setFullYear(today.getFullYear() + nextYearIndex);
        const nextYearFirstDay = new Date(targetYear.getFullYear(), 0, 1);

        if (nextYearFirstDay <= today) {
            setCurrentYearIndex(nextYearIndex);
            clearSelectedBar();
        }
    };

    // 선택된 바 해제 함수
    const clearSelectedBar = () => {
        setSelectedBarIndex(-1);
    };

    const yAxisData = calculateYAxisValues();

    return (
        <ContentArea sx={{ paddingBottom: '100px' }}>
            {/* 로딩 상태 */}
            {loading && (
                <CommonCard>
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                        {t('loading_data')}
                    </div>
                </CommonCard>
            )}

            {/* 에러 상태 */}
            {error && (
                <CommonCard>
                    <div style={{ textAlign: 'center', padding: '20px', color: 'red' }}>
                        {t('an_error_occurred_while_loading_data_message', { message: error })}
                    </div>
                </CommonCard>
            )}

            {/* 메인 콘텐츠 */}
            {!loading && !error && (
                <>
                    {/* 토글 버튼 */}
                    <Toggle
                        options={[t('weekly'), t('monthly'), t('yearly')]}
                        value={activeView}
                        onChange={(value) => {
                            setActiveView(value);
                            clearSelectedBar();
                        }}
                        sx={{
                            flexShrink: 0,
                            height: '40px',
                            minHeight: '40px',
                        }}
                    />

                    <CommonCard>
                        {/* 메인 카드 */}
                        <ReportCard
                            activeView={activeView === 1 ? 'month' : activeView === 2 ? 'year' : 'week'}
                            title={getViewTitle}
                            mainValue={`${calculateCurrentStats.minHeartRate} - ${calculateCurrentStats.maxHeartRate}`}
                            mainUnit={t('bpm')}
                            chartData={getViewData}
                            yAxisData={yAxisData}
                            monthDays={activeView === 1 ? getCurrentMonthDays : null}
                            selectedBarIndex={selectedBarIndex}
                            onBarSelect={setSelectedBarIndex}
                            onPreviousWeek={handlePreviousWeek}
                            onNextWeek={handleNextWeek}
                            onPreviousMonth={handlePreviousMonth}
                            onNextMonth={handleNextMonth}
                            onPreviousYear={handlePreviousYear}
                            onNextYear={handleNextYear}
                            isRangeData={true}
                        />
                    </CommonCard>
                </>
            )}
        </ContentArea>
    );
};

export default ReportHeartbeatUnion;